import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Heart, Loader2 } from "lucide-react";
import api from "../lib/api";

/* Stripe checkout return pages: /payment/success (polls status) and /payment/cancel. */

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [tx, setTx] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setTx(r.data); setState("paid"); return; }
        if (r.data.status === "expired") { setState("expired"); return; }
      } catch { /* keep polling */ }
      if (tries.current >= 12) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-20 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <h1 className="font-heading font-900 text-2xl">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-2">This usually takes a few seconds. Please don't close this page.</p>
        </>
      )}
      {state === "paid" && tx?.type === "membership" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
          <h1 className="font-heading font-900 text-3xl" data-testid="payment-success-title">Welcome to My Travel Hub!</h1>
          <p className="text-zinc-600 mt-3">Your <b className="capitalize">{(tx.plan || "").replace("_", " ")}</b> membership is active for the next year. Your journeys, documents and AI assistant are ready.</p>
          <Link to="/travel-hub" data-testid="goto-hub" className="inline-block mt-6 bg-indigo-700 text-white font-700 px-8 py-3 hover:bg-indigo-600 transition-colors">Open My Travel Hub</Link>
        </>
      )}
      {state === "paid" && tx?.type === "support" && (
        <>
          <Heart className="w-14 h-14 text-rose-600 fill-rose-600 mx-auto mb-4" />
          <h1 className="font-heading font-900 text-3xl" data-testid="payment-success-title">Thank you for supporting TravelerPulse!</h1>
          <p className="text-zinc-600 mt-3">Your contribution of <b>${((tx.amount || 0) / 100).toFixed(2)}</b> helps us build a better TravelerPulse for travelers everywhere.</p>
          {tx.bonus_granted && (
            <p className="mt-3 inline-block bg-emerald-50 border border-emerald-300 text-emerald-800 px-4 py-2 text-sm" data-testid="bonus-granted">
              🎁 A complimentary year of My Travel Hub Individual membership has been added to your account.
            </p>
          )}
          <div className="mt-6"><Link to="/travel-hub" className="text-indigo-700 font-700 hover:underline">Open My Travel Hub →</Link></div>
        </>
      )}
      {state === "expired" && (
        <>
          <XCircle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h1 className="font-heading font-900 text-2xl">Checkout session expired</h1>
          <p className="text-muted-foreground mt-2">No charge was made. You can try again anytime.</p>
          <Link to="/membership" className="inline-block mt-6 text-indigo-700 font-700 hover:underline">Back to membership plans →</Link>
        </>
      )}
      {state === "error" && (
        <>
          <XCircle className="w-14 h-14 text-rose-500 mx-auto mb-4" />
          <h1 className="font-heading font-900 text-2xl">We couldn't confirm the payment yet</h1>
          <p className="text-muted-foreground mt-2">If you completed the payment, it may still be processing — check your email receipt or your membership status in a few minutes.</p>
          <Link to="/membership" className="inline-block mt-6 text-indigo-700 font-700 hover:underline">Back to membership →</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-5 py-20 text-center" data-testid="payment-cancel-page">
      <XCircle className="w-14 h-14 text-zinc-400 mx-auto mb-4" />
      <h1 className="font-heading font-900 text-2xl">Payment cancelled</h1>
      <p className="text-muted-foreground mt-2">No charge was made. You can come back anytime.</p>
      <div className="mt-6 flex justify-center gap-6 text-sm">
        <Link to="/membership" className="text-indigo-700 font-700 hover:underline">Membership plans</Link>
        <Link to="/support" className="text-rose-600 font-700 hover:underline">Support TravelerPulse</Link>
      </div>
    </div>
  );
}
